import type { ReactNode } from "react";
import { useCallback } from "react";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { ButtonKind, ButtonSize, ButtonVariant, IconButton } from "@stubhub/broadway-web";
import { ChevronLeftIcon, ChevronRightIcon } from "@stubhub/broadway-web/icons/functional";
import { useContent } from "src/shared/components/content";
import { typedMemo } from "src/types/typedMemo";
import type {
  CarouselId,
  CarouselItemType,
} from "src/website-apps/shared/components/carousel-module/carousel-analytics-event";
import { CarouselItemSlot } from "./CarouselItemSlot";
import {
  arrowButtonContainer,
  arrowIconButtonClasses,
  carouselScrollContainer,
  carouselSpacer,
  carouselWrapper,
} from "./performer-carousel.recipe";
import { useCarouselScroll } from "./useCarouselScroll";
import { usePerformerCarouselPageEvents } from "./usePerformerCarouselPageEvents";

export interface PerformerCarouselProps<T> {
  items: readonly T[];
  renderItem: (item: T, index: number, onItemClick: (itemId: string | number) => void) => ReactNode;
  getItemKey: (item: T) => string | number;
  carouselId: CarouselId;
  itemType?: CarouselItemType | undefined;
}

/**
 * Horizontal scroll carousel for Recently Viewed Events.
 *
 * Desktop/tablet: Left/right arrow buttons with programmatic scrolling
 * Mobile: Native horizontal scroll with momentum, no arrow buttons
 */
const PerformerCarouselComponent = <T,>({
  items,
  renderItem,
  getItemKey,
  carouselId,
  itemType,
}: PerformerCarouselProps<T>) => {
  const isDesktop = useMediaQuery(Breakpoint.Desktop);
  const previousAriaContent = useContent("cid$#4141333#");
  const nextAriaContent = useContent("cid$#4141337#");
  const { scrollContainerRef, isAtStart, isAtEnd, scrollToPrev, scrollToNext } = useCarouselScroll();

  const { carouselInViewRef, fireArrowClickEvent, fireItemClick, fireItemHover, categoryId } =
    usePerformerCarouselPageEvents({
      scrollContainerRef,
      itemCount: items.length,
      isAtEnd,
      carouselId,
      itemType,
    });

  const handlePrevClick = useCallback(() => {
    scrollToPrev();
    fireArrowClickEvent("left");
  }, [scrollToPrev, fireArrowClickEvent]);

  const handleNextClick = useCallback(() => {
    scrollToNext();
    fireArrowClickEvent("right");
  }, [scrollToNext, fireArrowClickEvent]);
  const hidePaginationArrows = !isDesktop || (isAtStart && isAtEnd); // hide arrows if only 1 page of items or mobile

  return (
    <div className={carouselWrapper} ref={carouselInViewRef}>
      {/* Left arrow button (desktop/tablet only) */}
      {!hidePaginationArrows && (
        <div className={arrowButtonContainer}>
          <IconButton
            variant={ButtonVariant.Outline}
            icon={ChevronLeftIcon}
            onClick={handlePrevClick}
            aria-label={previousAriaContent}
            disabled={isAtStart}
            kind={ButtonKind.Neutral}
            size={ButtonSize.Small}
            classes={arrowIconButtonClasses}
          />
        </div>
      )}

      <div className={carouselScrollContainer} ref={scrollContainerRef}>
        {!isDesktop && <div className={carouselSpacer} />}
        {items.map((item, index) => {
          const itemId = getItemKey(item);
          return (
            <CarouselItemSlot
              key={itemId}
              itemId={itemId}
              itemIndex={index}
              itemCount={items.length}
              carouselId={carouselId}
              itemType={itemType}
              categoryId={categoryId}
              onItemHover={fireItemHover}
            >
              {renderItem(item, index, (clickedItemId: string | number) => {
                fireItemClick(clickedItemId, index);
              })}
            </CarouselItemSlot>
          );
        })}
        {!isDesktop && <div className={carouselSpacer} />}
      </div>

      {/* Right arrow button (desktop/tablet only) */}
      {!hidePaginationArrows && (
        <div className={arrowButtonContainer}>
          <IconButton
            variant={ButtonVariant.Outline}
            icon={ChevronRightIcon}
            onClick={handleNextClick}
            aria-label={nextAriaContent}
            disabled={isAtEnd}
            kind={ButtonKind.Neutral}
            size={ButtonSize.Small}
            classes={arrowIconButtonClasses}
          />
        </div>
      )}
    </div>
  );
};

export const PerformerCarousel = typedMemo(PerformerCarouselComponent);
